import { AxiosRequestConfig } from 'axios';

import config from '../../../../projectConfig/index';
import Cookies from 'js-cookie';

/**
 * The base url of the api server's endpoint needs to be configured here.
 *
 * NOTE: This has to be manged by a build flavour configuration files
 * such as environment fils or via a remote configuration manger.
 */
const API_SERVER_BASE_URL = config.CRESIVOX_BASE_URL;
/**
 * The request timeout of the api server needs to be configured here.
 *
 * NOTE: This has to be manged by a build flavour configuration files
 * such as environment fils  or via a remote configuration manger.
 */
const API_SERVER_REQUEST_TIMEOUT = config.ONECLICK_API_SERVER_TIMEOUT;

/**
 * All basic axios request-configurations needs to be set here.
 * This will used inside the services/api/index.ts file while
 * creating axios service instance to handle api calls.
 */
export const axiosRequestConfig: AxiosRequestConfig =
  Object.freeze<AxiosRequestConfig>({
    // withCredentials: true,
    baseURL: API_SERVER_BASE_URL,

    /**
     * The timeout for axios is set in milliseconds.
     * Here, it's set to 15 seconds (15000ms) as the default value if
     * the API_SERVER_REQUEST_TIMEOUT is not set in the
     * environment files.
     */
    // timeout: API_SERVER_REQUEST_TIMEOUT || 15000,
  });

/**
 * All the application service api endpoints should be defined here and never
 * directly define and use apiEndpoints as the baseURL is configured based on
 * the build flavour or other remote configuration managers.
 *
 * While defining endpoints here, kindly note that the part after the base url
 * should be added here and shouldn't include the host/baseURL part.
 *
 * Kindly refer the below examples for more details:
 *
 * If the endpoint is "https://dev.exampleapiserver.tld/api/v1/login" , then it
 * should be split as below:
 * API_SERVER_BASE_URL =  "https://dev.exampleapiserver.tld/api/v1"
 * apiEndpoints = {
 *     authentication: {
 *         login: "/login"
 *     }
 * }
 *
 */
export const apiEndpoints = Object.freeze({
  auth: {
    authenticate: '',
  },

  user: {
    userProfileDetails: () => {
      return `/user/profile`;
    },

    userLogin: () => {
      return `/user/login`;
    },
    userRegister: () => {
      return `/user/register`;
    },
    sendEmailOtp: () => {
      return `/user/send-email-otp`;
    },
    developerRegister: () => {
      return `/user/add-developer`;
    },

    userLoginWithGoogle: () => {
      return `/user/login`;
    },

    userForgetPassword: () => {
      return `user/forget-password`;
    },

    userChangePassword: () => {
      return `user/change-password`;
    },
    updateUserProfile: () => {
      return `user/update-profile`;
    },
    markOnboardingComplete: () => {
      return `user/onboarding-complete`;
    },
    userResetPassword: () => {
      return `user/reset-password`;
    },
    activateDeactivateUser: (userId: string) => {
      return `user/update-status/${userId}`;
    },
    deleteAccount: () => {
      return `user/delete-account`;
    },
     getDeletedUsersList: () => {
      return `user/deleted-users`;
    },
  },
  product: {
    updateProduct: (productId: string) => {
      return `product/update-product/${productId}`;
    },
    deleteProduct: () => {
      return `product/delete-product`;
    },
    getProductDetails: (productId: string) => {
      return `product/get-product/${productId}`;
    },
    createProduct: () => {
      return `product/add-product`;
    },

    // listProducts: () => {
    //   return `product/get-products`;
    // },

    // listProducts: (page: number, limit: number) => {
    //   return `product/get-products?page=${page}&limit=${limit}`;
    // },

    listProducts: (
      page: number,
      limit: number,
      search?: string,
      title?: string,
      brand?: string
    ) => {
      let url = `product/get-products?page=${page}&limit=${limit}`;

      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      if (title) {
        url += `&title=${encodeURIComponent(title)}`;
      }
      if (brand) {
        url += `&brand=${encodeURIComponent(brand)}`;
      }

      return url;
    },
    importBulkProduct: () => {
      return `product/import-bulk-product`;
    },
    updateProductStatus: (productId: string) => {
      return `product/update-product-status/${productId}`;
    },
    getBestSellingProduct: (vendorId: string) => {
      return `/product/best-seller-product-list/${vendorId}`;
    },
    uploadImage: () => {
      return `/product/upload-image`;
    },
  },
  order: {
    getOrderList: (status: string, page?: number, limit?: number) => {
      const params = new URLSearchParams();
      params.set('status', status);
      if (typeof page === 'number') params.set('page', String(page));
      if (typeof limit === 'number') params.set('limit', String(limit));
      return `order/get-orders?${params.toString()}`;
    },
    getOrderById: (orderId: string) => {
      return `order/get-order/${orderId}`;
    },
    updateOrder: (orderId: string) => {
      return `order/update-order/${orderId}`;
    },
    deleteOrder: (orderId: string) => {
      return `order/delete-order/${orderId}`;
    },
    createOrder: () => {
      return `order/create-order`;
    },
    forwardOrderToWHolesaler: (orderId: string) => {
      return `order/forward-order-to-wholesaler/${orderId}`;
    },
    getForwardOrderItems: (orderId: string) => {
      return `order/forward-order-items/${orderId}`;
    },
  },
  category: {
    addCategory: () => {
      return `category/add-product-category`;
    },
    getCategoryList: (page?: number, limit?: number, search?: string) => {
      const params = new URLSearchParams();
      if (typeof page === 'number') params.set('page', String(page));
      if (typeof limit === 'number') params.set('limit', String(limit));
      if (search?.trim()) params.set('search', search.trim());
      const query = params.toString();
      return query ? `category/get-category?${query}` : 'category/get-category';
    },
    getCategoryById: (orderId: string) => {
      return `category/get-category/${orderId}`;
    },
    updateCategory: (orderId: string) => {
      return `category/update-category/${orderId}`;
    },
    deleteCategory: () => {
      return `category/delete-category`;
    },
    getCategoryTree: () => {
      return `category/tree`;
    },
  },
  subcategory: {
    addsubCategory: () => {
      return `category/add-sub-category`;
    },
    getSubCategoryList: () => {
      return `category/sub-category`;
    },
    getSubCategoryById: (orderId: string) => {
      return `category/sub-category/${orderId}`;
    },
    updateSubCategory: (orderId: string) => {
      return `category/update-sub-category/${orderId}`;
    },
    deleteSubCategory: () => {
      return `category/delete-sub-category`;
    },
  },
  brand: {
    addBrand: () => {
      return `brand/add-brand`;
    },
    getBrandList: (page?: number, limit?: number, search?: string) => {
      const params = new URLSearchParams();
      if (typeof page === 'number') params.set('page', String(page));
      if (typeof limit === 'number') params.set('limit', String(limit));
      if (search?.trim()) params.set('search', search.trim());
      const query = params.toString();
      return query ? `brand/get-brands?${query}` : 'brand/get-brands';
    },
    getBrandById: (brandId: string) => {
      return `brand/get-brand/${brandId}`;
    },
    updateBrand: (brandId: string) => {
      return `brand/update-brand/${brandId}`;
    },
    deleteBrand: (brandId: string) => {
      return `brand/delete-brand/${brandId}`;
    },
  },

  unit: {
    addUnit: () => {
      return `unit/add-unit`;
    },
    getUnitList: () => {
      return `unit/get-units`;
    },
    getUnitById: (unitId: string) => {
      return `unit/get-unit/${unitId}`;
    },
    updateUnit: (unitId: string) => {
      return `unit/update-unit/${unitId}`;
    },
    deleteUnit: (unitId: string) => {
      return `unit/delete-unit/${unitId}`;
    },
  },
  review: {
    getReviewList: () => {
      return `product/get-reviews`;
    },
  },

  invoice: {
    getInvoiceList: () => {
      return `invoice/invoices`;
    },
    getInvoiceById: (invoiceId: string) => {
      return `invoice/invoice/${invoiceId}`;
    },
    updateInvoice: (invoiceId: string) => {
      return `invoice/update-invoice/${invoiceId}`;
    },
    deleteInvoice: (invoiceId: string) => {
      return `invoice/delete-invoice/${invoiceId}`;
    },
    createInvoice: () => {
      return `invoice/create-invoice`;
    },
  },

  customer: {
    getCustomerList: (page?: number, limit?: number, search?: string) => {
      const params = new URLSearchParams();
      if (typeof page === 'number') params.set('page', String(page));
      if (typeof limit === 'number') params.set('limit', String(limit));
      if (search?.trim()) params.set('search', search.trim());

      const query = params.toString();
      return query ? `user/customers?${query}` : 'user/customers';
    },
    getCustomerById: (customerId: string) => {
      return `user/customer/${customerId}`;
    },
    updateCustomer: (customerId: string) => {
      return `user/update-customer/${customerId}`;
    },
    deleteCustomer: (customerId: string) => {
      return `user/delete-customer/${customerId}`;
    },
    createCutomer: () => {
      return `user/add-customer`;
    },
    searchCustomerList: (search: string, page?: number, limit?: number) =>
      apiEndpoints.customer.getCustomerList(page, limit, search),
  },
  refund: {
    getRefundList: (status: string) => {
      return `order/refund-list?status=${status}`;
    },
    updateRefund: (refundId: string) => {
      return `order/update-refund/${refundId}`;
    },
    getRefundById: (refundId: string) => {
      return `order/refund/${refundId}`;
    },
  },
  banner: {
    getBannerList: () => {
      return `banner/banners`;
    },
    getBannerById: (bannerId: string) => {
      return `banner/banner/${bannerId}`;
    },
    updateBanner: (bannerId: string) => {
      return `banner/update-banner/${bannerId}`;
    },
    deleteBanner: (bannerId: string) => {
      return `banner/delete-banner/${bannerId}`;
    },
    createBanner: () => {
      return `banner/add-banner`;
    },
    updateBannerStatus: (bannerId: string) => {
      return `banner/update-banner-status/${bannerId}`;
    },
    getWholesalerBanner: (wholesalerId: string) => {
      return `/banner/banner-list/${wholesalerId}`;
    },
    getAllWholesalerBannerWithoughtId: () => {
      return `banner/wholesaler-banner-list`;
    },
  },
  attribute: {
    getAttributeList: () => {
      return `attribute/attributes`;
    },
    getAttributeById: (attributeId: string) => {
      return `attribute/attribute/${attributeId}`;
    },
    updateAttribute: (attributeId: string) => {
      return `attribute/update-attribute/${attributeId}`;
    },
    deleteAttribute: (attributeId: string) => {
      return `attribute/delete-attribute/${attributeId}`;
    },
    createAttribute: () => {
      return `attribute/add-attribute`;
    },
  },
  coupon: {
    getCouponList: () => {
      return `coupon/coupons`;
    },
    getCouponById: (couponId: string) => {
      return `coupon/coupon/${couponId}`;
    },
    updateCoupon: (couponId: string) => {
      return `coupon/update-coupon/${couponId}`;
    },
    deleteCoupon: (couponId: string) => {
      return `coupon/delete-coupon/${couponId}`;
    },
    createCoupon: () => {
      return `coupon/add-coupon`;
    },
  },

  wholesaler: {
    // getWholesalerList: (search: string) => {
    //   return `user/Wholesalers?search=${search}`;
    // },
    getWholesalerList: (search: string, filter?: string) => {
      const params = new URLSearchParams();
      if (filter && filter.trim()) params.set('filter', filter.trim());

      if (!search || search === 'undefined') search = ''; // optional safeguard
      params.set('search', search);

      return `user/wholesalers?${params.toString()}`;
    },

    checkWholesalerConnectionStatus: (wholesalerId: string) => {
      return `user/check-connection-with-wholesaler/${wholesalerId}`;
    },
    getWholesalerById: () => {
      return `user/Wholesaler`;
    },
    updateWholesaler: () => {
      return `user/update-Wholesaler`;
    },
    updateWholesalerDucmentStatus: (wholesalerId: string) => {
      return `user/update-document-status/${wholesalerId}`;
    },
    updateWholesalerForResaller: (wholesalerId: string) => {
      return `user/update-reseller-activation/${wholesalerId}`;
    },
    deleteWholesaler: (wholesalerId: string) => {
      return `user/wholesaler/${wholesalerId}`;
    },
    createWholesaler: () => {
      return `user/add-wholesaler`;
    },
    connectRequestWithWholesaler: () => {
      return `user/connect-with-wholesaler`;
    },
    updateApprovalStatus: (connectionId: string) => {
      return `user/update-connection-status/${connectionId}`;
    },
    getAllConnections: (status: string) => {
      return `user/resaler-request-list?status=${status}`;
    },
    // getAllWholesalerProductList: (wholesalerId: string, page: number, limit: number) => {
    //   return `product/wholesaler-product-list/${wholesalerId}?page=${page}&limit=${limit}`;
    // },

    getAllWholesalerProductList: (
      wholesalerId: string,
      page: number,
      limit: number,
      search?: string,
      title?: string,
      brand?: string
    ) => {
      let url = `product/wholesaler-product-list/${wholesalerId}?page=${page}&limit=${limit}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (title) {
        url += `&title=${encodeURIComponent(title)}`;
      }
      if (brand) {
        url += `&brand=${encodeURIComponent(brand)}`;
      }
      return url;
    },
    importWholesalerProduct: (wholesalerId: string) => {
      return `product/import-wholesaler-product/${wholesalerId}`;
    },
    getConnectionStatus: () => {
      return `user/connection-Status/`;
    },
    importWholesalerBulkProduct: (
      wholesalerId: string,
      data: { increaseprice: string; resellerId: string }
    ) => {
      return `product/import-wholesaler-bulk-product/${wholesalerId}`;
    },
  },

  resellerImport: {
    importProductForReseller: (
      wholesalerId: string,
      data: { increaseprice: string; resellerId: string }
    ) => {
      return `product/admin/import-wholesaler-bulk-product/${wholesalerId}`;
    },

    // getImportResellerList:()=>{
    //      return `product/get-importproducts-deatils`;
    // }

    getImportResellerList: (search?: string) => {
      let url = `product/get-importproducts-deatils`;

      if (search) {
        url += `?search=${encodeURIComponent(search)}`;
      }

      return url;
    },
  },

  resellerSubscriptionEndPoint: {
    addResellerPlane: () => {
      return `logo/admin/vendor/assign-plan`;
    },
    getResellerSubscriptionList: (search?: string) => {
      // return `logo/admin/vendor/plan`;
      let url = `logo/admin/vendor/plan`;
      if (search) {
        url += `?search=${encodeURIComponent(search)}`;
      }
      return url;
    },
  },

  logo: {
    getLogoList: () => {
      return `logo/Logos`;
    },
    updateLogo: (logoId: string) => {
      return `logo/update-Logo/${logoId}`;
    },
    deleteLogo: (logoId: string) => {
      return `logo/Logo/${logoId}`;
    },
    createLogo: () => {
      return `logo/add-logo`;
    },
    updateTheme: (logoId: string) => {
      return `logo/update-theme/${logoId}`;
    },
  },

  pluginsetup: {
    addPlugin: `plugin/add-plugin`,
    getPlugin: `plugin/get-plugins`,
    addGoogleAnalytics: `ga4-config/save2DB`,
    getGoogleAnalytics: `ga4-config/getfromDB`,
    getGooglePixel: `ga4/events/weekly`,
    getAnalyticsDashboard: `ga4/analytics-dashboard`,
    addWhatsAppPlugin: 'two-factor/setup-2fa',
    getWhatsAppPlugin: 'two-factor/get-2fa',
    addTwilioPlugin: 'analytics/get-analytics-user',
    addTwoFactorPlugin: 'analytics/get-analytics-user',
    addOpenAi: `openaipulgin/setKey`,
    getOpenAi: `openaipulgin/getKey`,
    AddGooglePlugIn:`user/google-plugin/save2DB`,
    GetGooglePlugIn:`user/google-plugin/getfromDB`,
    getCRMStatus: () => 'plugin/crm/status',
    purchaseCRM: () => 'plugin/crm/purchase',

  },
  // plugin/get-plugins

  pageService: {
    getAllTabs: () => {
      return `/page-service`;
    }, // Endpoint to get all tabs
    getTabData: (tabId: string) => {
      return `/page-service/${tabId}`;
    }, // Endpoint to get a specific tab by ID
    createOrUpdateTab: (tabId: string) => {
      return `/page-service/${tabId}`;
    }, // Endpoint to create or update a tab
    deleteTab: (tabId: string) => {
      return `/page-service/${tabId}`;
    }, // Endpoint to delete a tab
    getTermData: (tabId: string) => {
      return `/page-service/${tabId}/88e06cdb-bdde-11ef-ad07-00163c34c678`;
    },
  },

  community: {
    getCommunityList: () => {
      return `community/communities`;
    },
    getCommunityById: (communityId: string) => {
      return `community/${communityId}`;
    },
    createCommunity: () => {
      return `community/add-community`;
    },
    updateCommunity: (communityId: string) => {
      return `community/update/${communityId}`;
    },
    deleteCommunity: (communityId: string) => {
      return `community/delete-community/${communityId}`;
    },
    updateCommunityStatus: (communityId: string) => {
      return `community/update-status/${communityId}`;
    },
    getWholesalerCommunity: (wholesalerId: string) => {
      return `community/wholesaler/${wholesalerId}`;
    },
    getAllWholesalerCommunityWithoutId: () => {
      return `community/wholesaler/list`;
    },
  },

  communityforVideos: {
    getCommunityListVideos: (wholesalerId: string) => {
      return `community/community-list/${wholesalerId}`;
    },
  },
  storyProduct: {
    getStoryProductList: () => {
      return `storyproduct/stories`;
    },
    getAllStoryProductList: () => {
      return `storyproduct/storyproduct-list`;
    },
    getStoryProductById: (storyProductId: string) => {
      return `storyproduct/${storyProductId}`;
    },
    createStoryProduct: () => {
      return `storyproduct/add-storyproduct`;
    },
    updateStoryProduct: (storyProductId: string) => {
      return `storyproduct/update/${storyProductId}`;
    },
    deleteStoryProduct: (storyProductId: string) => {
      return `storyproduct/delete-storyproduct/${storyProductId}`;
    },
    updateStoryProductStatus: (storyProductId: string) => {
      return `storyproduct/update-status/${storyProductId}`;
    },
    getWholesalerStoryProduct: (wholesalerId: string) => {
      return `storyproduct/wholesaler/${wholesalerId}`;
    },
    getAllWholesalerStoryProductWithoutId: () => {
      return `storyproduct/wholesaler/list`;
    },
  },

  delivery: {
    getDeliveryList: () => {
      return `delivery/delivery-man-list`;
    },
    updateDelivery: (userId: string) => {
      return `delivery/delivery-man/${userId}`;
    },
    deleteDelivery: (userId: string) => {
      return `delivery/delivery-man/${userId}`;
    },
    addDelivery: () => {
      return `delivery/add-delivery-man`;
    },
    addDeliveryManOrder: (orderId: string, deliveryManId: string) => {
      return `delivery/assign-deliveryman-to-order/${orderId}/${deliveryManId}`;
    },
    getDeliveryManOrderList: () => {
      return `delivery/orders-by-deliveryman`;
    },
    updateOrderStatus: () => {
      return `delivery/update-order-status-by-deliveryman`;
    },
  },

  generalSetup: {
    addDomain: () => {
      return `logo/add-domain`;
    },
    calculateShippingCharged: () => {
      return `shipment/calculate-charges-public`;
    },
    updateDomain: () => {
      return `logo/update-domain`;
    },
    generateUniqueDomain: () => {
      return `logo/random-domain`;
    },
    getDomain: () => {
      return `logo/logos`;
    },
  },

  testimonial: {
    addTestimonial: () => {
      return `review/add-review`;
    },
    updateTestimonial: (reviewId: string) => {
      return `review/update-review/${reviewId}`;
    },
    deleteTestimonial: (testimonialId: string) => {
      return `review/delete-review/${testimonialId}`;
    },
    getTestimonial: () => {
      return `review/get-reviews`;
    },
  },

  purchaseOrder: {
    createOrder: () => {
      return `po-page/add-po`;
    },
  },
  purchaseOrderList: {
    getPurchaseOrderList: () => {
      return `po-page/purchase-orders-list`;
    },
  },

  addPurchaseOrderData: {
    addPruchaseOrderProduct: () => {
      return 'po-page/add-po-product';
    },
    getPruchaseOrderProduct: () => {
      return 'po-page/po-purchase-orders-list';
    },
  },

  addBillingDetails: {
    addBillingDetails: () => {
      return `billing-invoice/add`;
    },
  },

  getBillingDetailsList: {
    getBillingDetailsList: () => {
      return `billing-invoice/billing-invoice-list`;
    },
  },

  socialMedia: {
    addSocialMedia: () => {
      return `social-media/add`;
    },
    deleteSocialMedia: (socialId: string) => {
      return `social-media/${socialId}`;
    },
    getSocialMedia: () => {
      return `social-media`;
    },
  },

  // reseller: {
  //   getResellerList: () => {
  //     return `user/resellers`;
  //   },
  // },
  reseller: {
    getResellerList: (page?: number, limit?: number, search?: string) => {
      const params = new URLSearchParams();
      if (typeof page === 'number') params.set('page', String(page));
      if (typeof limit === 'number') params.set('limit', String(limit));
      if (search?.trim()) params.set('search', search.trim());
      const query = params.toString();
      return query ? `/user/resellers?${query}` : '/user/resellers';
    },
    getSearchResellerList: (search?: string) => {
      let url = `/product/get-importproducts-deatils`;
      if (search) {
        url += `?search=${encodeURIComponent(search)}`;
      }
      return url;
    },
    // New endpoint for search
    searchResellerList: (search: string, page?: number, limit?: number) =>
      apiEndpoints.reseller.getResellerList(page, limit, search),
  },

  liveSale: {
    addliveSale: () => {
      return `live-sale/create`;
    },

    updateliveSale: (liveSaleId: string) => {
      return `live-sale/update/${liveSaleId}`;
    },
    updateliveSalestatus: (liveSaleId: string) => {
      return `live-sale/update-status/${liveSaleId}`;
    },
    getLiveSaleById: (liveSaleId: string) => {
      return `live-sale/${liveSaleId}`;
    },

    deleteliveSale: (liveSaleId: string) => {
      return `live-sale/delete/${liveSaleId}`;
    },
    getAllLiveSales: () => {
      return `/live-sale/all/vendor`;
    },
    getActiveLiveSales: () => {
      return `/live-sale/active/vendor`;
    },
  },

  featuredSale: {
    createFeaturedDeal: () => {
      return `/featured-deals/create-deal`;
    },

    updateFeaturedDeal: (dealId: string) => {
      return `/featured-deals/update/${dealId}`;
    },

    toggleFeaturedDealStatus: (dealId: string) => {
      return `/featured-deals/update/${dealId}/status`;
    },

    getFeaturedDealById: (dealId: string) => {
      return `/featured-deals/get-featured/${dealId}`;
    },

    deleteFeaturedDeal: (dealId: string) => {
      return `/featured-deals/delete/${dealId}`;
    },

    getAllFeaturedDeals: () => {
      return `/featured-deals/get-featured`;
    },

    addProductToDeal: (dealId: string) => {
      return `/featured-deals/${dealId}/add-product`;
    },
    removeProductFromDeal: (dealId: string, productId: string) => {
      return `/featured-deals/${dealId}/remove-product/${productId}`;
    },
    getDealProducts: (dealId: string) => {
      return `/featured-deals/${dealId}/products`;
    },
  },

  dealOfTheDay: {
    createDeal: () => {
      return `/dealDay/add-dealDay`;
    },

    updateDeal: (dealdayId: string) => {
      return `/dealDay/update-dealDay/${dealdayId}`;
    },

    updateDealStatus: (dealdayId: string) => {
      return `/dealDay/${dealdayId}/status`;
    },

    // getDealById: (dealdayId: string) => {
    //   return `/dealDay/get-dealDay/${dealdayId}`;
    // },

    deleteDeal: (dealdayId: string) => {
      return `/dealDay/delete/${dealdayId}`;
    },

    getAllDeals: () => {
      return `/dealDay/get-dealDay`;
    },

    getActiveDeal: () => {
      return `/dealDay/active`;
    },
  },

  employees: {
    addEmployee: () => {
      return `user/employee`;
    },
    updateEmployee: (employeeId: string) => {
      return `user/employee/${employeeId}`;
    },
    deleteEmployees: (employeeId: string) => {
      return `user/employee/${employeeId}`;
    },
    getEmployeeList: () => {
      return `user/employees`;
    },
  },
  theme: {
    addTheme: () => {
      return `theme/add-theme`;
    },
    getAllThemeList: () => {
      return `theme/themes`;
    },
  },

  amountLock: {
    addLockAmount: (vendorId: string) => {
      return `wallet/users/${vendorId}/lockblance`;
    },
    getLockAmount: () => {
      return `wallet/vendors-lockbalance-wallets`;
    },
  },
  plugin: {
    addPaymentGateway: () => {
      return `payment-gateway/add`;
    },
    getPaymentGateway: () => {
      return `payment-gateway`;
    },
    getRazorpayOAuthStartUrl: (mode: string) => {
      return `payment-gateway/razorpay/oauth/start?mode=${encodeURIComponent(mode)}`;
    },
    saveRazorpayConfig: () => {
      return `payment-gateway/razorpay/config`;
    },
    disconnectRazorpayOAuth: () => {
      return `payment-gateway/razorpay/disconnect`;
    },
  },
  shiprocket: {
    addShiprocketUser: () => {
      return `shiprocket/create-user`;
    },
    getShiprocketUser: (vendorId: any) => {
      return `shiprocket/get-user/${vendorId}`;
    },
    createShipRocket: (vendorId: string) => {
      return `shiprocket/create-order/${vendorId}`;
    },
    getShipRocketDetail: (orderId: string) => {
      return `shiprocket/get-order-details/${orderId}`;
    },
    shipRocketOrderTrack: (orderId: string) => {
      return `shiprocket/get-order-track/${orderId}`;
    },
  },
  // https://testabc.apkaprachar.com/api/shiprocket/get-order-track/:shipment_id

  subscriptionPlan: {
    addSubscriptionPlan: () => {
      return `subscription/add-plan`;
    },
    updateSubscriptionPlan: (planId: string) => {
      return `subscription/update-plan/${planId}`;
    },
    deleteSubscriptionPlan: (planId: string) => {
      return `subscription/delete-plan/${planId}`;
    },
    getSubscriptionPlan: () => {
      return `subscription/plans`;
    },
    getActivationStatus: () => {
      return `subscription/activation-status`;
    },
    subscribePlan: (planId: string) => {
      return `subscription/plan-activate/${planId}`;
    },
    verifyPayment: (transactionId: string) => {
      return `subscription/verify-payment/${transactionId}`;
    },

    getAllTransactionListOfSubscriber: () => {
      return `/subscription/subscriber-transaction-list`;
    },
    applyCouponForActivatePlan: (planId: string) => {
      return `/subscription/appyly-coupon-for-plan-activate/${planId}`;
    },
    setAutoRenew: (planId: string) => {
      return `/subscription/subscription/${planId}/enable-autorenew`;
    },
    processAutoRenew: () => {
      return `/subscription/autorenew/process`;
    },
    verifyReferalCode: (refferalCode: string) => {
      return `/user/verify-refferal-code/${refferalCode}`;
    },
  },
  wallet: {
    addMoneyToWallet: () => {
      return `/wallet/add`;
    },
    createWalletTopUp: () => {
      return `/wallet/top-up`;
    },
    verifyPayment: (transactionId: string) => {
      return `/wallet/verify-payment/${transactionId}`;
    },
    getWalletTopUpStatus: (transactionId: string) => {
      return `/wallet/top-up/${transactionId}`;
    },
    verifyBuildAppPayment: (transactionId: string) => {
      return `app-payment-verify/${transactionId}`;
    },
    getAllWalletTransactionList: () => {
      return `/wallet/wallet-transaction`;
    },
    getWalletPaymentGateway: () => {
      return `/wallet/payment-gateway`;
    },
    getWalletPaymentMethods: () => {
      return `/wallet/payment-methods`;
    },
    tokenizeWalletCard: () => {
      return `/wallet/payment-methods/card/tokenize`;
    },
    createWalletUpiMandate: () => {
      return `/wallet/payment-methods/upi/mandate`;
    },
    setDefaultWalletPaymentMethod: (paymentMethodId: string) => {
      return `/wallet/payment-methods/${paymentMethodId}/default`;
    },
    deleteWalletPaymentMethod: (paymentMethodId: string) => {
      return `/wallet/payment-methods/${paymentMethodId}`;
    },
    setupWalletAutopay: () => {
      return `/wallet/autopay/setup`;
    },
    getWalletAutopayStatus: () => {
      return `/wallet/autopay/status`;
    },
    cancelWalletAutopay: () => {
      return `/wallet/autopay/cancel`;
    },
    moneyTransfer: () => {
      return `/wallet/money-transfer`;
    },
    getMinimumWalletAmount: () => {
      return `/wallet/minimum-wallet-amount`;
    },
    withdrawAmount: () => {
      return `/wallet/withdraw`;
    },
    getHoldMoneyInfo: () => {
      return `/wallet/hold-money`;
    },
    updateStatusOfWithdrawRequest: (id: string) => {
      return `/wallet/update-withdraw-status/${id}`;
    },
  },
  beneficiary: {
    addbeneficiary: () => {
      return `/beneficiary/add-beneficiary`;
    },
    updateBeneficiary: (beneficaryId: string) => {
      return `/beneficiary/update-beneficiary/${beneficaryId}`;
    },
    getAllBeneficiary: () => {
      return `/beneficiary`;
    },
    deleteBeneficary: (beneficaryId: string) => {
      return `/beneficiary/delete-beneficiary/${beneficaryId}`;
    },
  },
  businessCategory: {
    getBusinessCategoryList: () => {
      return `business-category/all`;
    },
    deleteBusinessCategory: (id: string) => {
      return `business-category/delete/${id}`;
    },
    createBusinessCategory: () => {
      return `business-category/add`;
    },
  },
  superAdminRecharged: {
    addSuperAdminRecharged: () => {
      return 'wallet/superadmin-recharge';
    },
    getSuperAdminRecharged: () => {
      return 'wallet/vendors-wallets';
    },
  },

  proFormaInvoiceForm: {
    addProFormaInvoiceForm: () => {
      return 'po-page/add-proform-invoices';
    },

    getProFormaInvoiceForm: () => {
      return 'po-page/po-proform-invoices-list';
    },

    addProFormaDataList: () => {
      return 'po-page/add-po-proform-invoices';
    },

    getProFormaDataList: () => {
      return 'po-page/po-purchase-orders-proform-invoices-list';
    },
  },

  productReportData: {
    getProductReportData: () => {
      return 'product/product-report';
    },
  },
  orderReportData: {
    getOrdertReportData: () => {
      return 'order/orders-report';
    },
  },

  saleReportData: {
    getSaleReportData: () => {
      return 'order/sales-report';
    },
  },
  addThemeFunction: {
    addThemeReacharge: () => {
      return 'theme/add-theme-price';
    },

    getAllRechargedThemeList: () => {
      return 'theme/themes-list';
    },

    getAllMobileViewThemeList: () => {
      return 'theme/mobile-themes-list';
    },
  },
  purchaseThemeFunction: {
    themePurchase: (themeName: string) => {
      return `theme/install-theme/${themeName}`;
    },
  },

  adminThemePurchase: {
    adminThemeSaleList: () => {
      return `theme/admin/theme-purchases`;
    },
  },
  userThemeInstalled: {
    userThemeInstalled: () => {
      return `theme/user/themes-installed`;
    },
  },
  themeDeleteFunction: {
    themeDelete: (id: string) => {
      return `theme/delete-theme/${id}`;
    },
  },
  ProjectUpload: {
    AddProject: () => {
      return `zip/upload-project`;
    },

    GetProject: (vendorId: string) => {
      return `zip/get-projects/${vendorId}`;
    },
    // zip/getall-projects

    GetAdminAllProject: () => {
      return `zip/getall-projects`;
    },

    UpdateProjectStatus: (projectId: string) => {
      return `zip/update-project/${projectId}`;
    },

    deleteProject: () => {
      return `zip/delete-project`;
    },
  },

  facebookAccount: {
    createFacebookAccount: () => {
      return `facebook/save-fb-details`;
    },
    getAllFacebookAccount: () => {
      return `facebook/get-fb-details`;
    },
  },

  fbCampaignPage: {
    fbcampaignList: () => {
      return `facebook/get-campaigns`;
    },

    fbaddCampaign: () => {
      return `facebook/campaign`;
    },
  },

  facebookAdsPage: {
    facebookAdsList: () => {
      return `facebook/get-ads`;
    },

    facebookAdsAdd: () => {
      return `facebook/create-ad`;
    },
  },

  facebookAdsetPage: {
    facebookAdsetList: () => {
      return `facebook/get-adsets`;
    },

    facebookAdsetAdd: () => {
      return `facebook/adset`;
    },
  },

  facebookCreativePage: {
    facebookCreativeList: () => {
      return `facebook/get-creative`;
    },

    facebookCreativeAdd: () => {
      return `facebook/creative`;
    },
  },

  fbLeadsPage: {
    getLeads: (status?: string, page?: number, limit?: number) => {
      const p = new URLSearchParams();
      if (status) p.set('status', status);
      if (page) p.set('page', String(page));
      if (limit) p.set('limit', String(limit));
      const q = p.toString();
      return q ? `facebook/leads?${q}` : 'facebook/leads';
    },
    getLeadForms: () => 'facebook/lead-forms',
    importLeads: () => 'facebook/import-leads',
    syncLeads: () => 'facebook/sync-leads',
    getLeadById: (id: string) => `facebook/leads/${id}`,
    updateLead: (id: string) => `facebook/leads/${id}`,
    deleteLead: (id: string) => `facebook/leads/${id}`,
    addActivity: (id: string) => `facebook/leads/${id}/activities`,
  },

  ticketsPage: {
    create: () => 'tickets',
    getMine: (page?: number, pageSize?: number) => {
      const p = new URLSearchParams();
      if (page) p.set('page', String(page));
      if (pageSize) p.set('pageSize', String(pageSize));
      const q = p.toString();
      return q ? `tickets/mine?${q}` : 'tickets/mine';
    },
    getAll: (params?: { status?: string; priority?: string; category?: string; assignedAgentId?: string; page?: number; pageSize?: number }) => {
      const p = new URLSearchParams();
      if (params?.status) p.set('status', params.status);
      if (params?.priority) p.set('priority', params.priority);
      if (params?.category) p.set('category', params.category);
      if (params?.assignedAgentId) p.set('assignedAgentId', params.assignedAgentId);
      if (params?.page) p.set('page', String(params.page));
      if (params?.pageSize) p.set('pageSize', String(params.pageSize));
      const q = p.toString();
      return q ? `tickets?${q}` : 'tickets';
    },
    getStats: () => 'tickets/stats',
    getById: (id: string) => `tickets/${id}`,
    reply: (id: string) => `tickets/${id}/reply`,
    updateStatus: (id: string) => `tickets/${id}/status`,
    updatePriority: (id: string) => `tickets/${id}/priority`,
    assign: (id: string) => `tickets/${id}/assign`,
  },

  crm: {
    getLeads: (status?: string, source?: string, assigned_to?: string, search?: string, page?: number, limit?: number, date_from?: string, date_to?: string, tag_id?: string, form?: string, sort_by?: string, sort_dir?: string, view?: string, unassigned?: boolean) => {
      const p = new URLSearchParams();
      if (status) p.set('status', status);
      if (source) p.set('source', source);
      if (assigned_to) p.set('assigned_to', assigned_to);
      if (search) p.set('search', search);
      if (page) p.set('page', String(page));
      if (limit) p.set('limit', String(limit));
      if (date_from) p.set('date_from', date_from);
      if (date_to) p.set('date_to', date_to);
      if (tag_id) p.set('tag_id', tag_id);
      if (form) p.set('form', form);
      if (sort_by) p.set('sort_by', sort_by);
      if (sort_dir) p.set('sort_dir', sort_dir);
      if (view) p.set('view', view);
      if (unassigned) p.set('unassigned', 'true');
      const q = p.toString();
      return q ? `crm/leads?${q}` : 'crm/leads';
    },
    getLeadViewCounts: () => 'crm/leads/view-counts',
    getLeadFormNames: () => 'crm/leads/form-names',
    getLeadSources: () => 'crm/leads/sources',
    getLead: (id: string) => `crm/leads/${id}`,
    createLead: () => 'crm/leads',
    updateLead: (id: string) => `crm/leads/${id}`,
    deleteLead: (id: string) => `crm/leads/${id}`,
    bulkImportLeads: () => 'crm/leads/bulk-import',
    assignLead: (id: string) => `crm/leads/${id}/assign`,
    logCall: (id: string) => `crm/leads/${id}/call-log`,
    callRecording: (id: string, logId: string) => `crm/leads/${id}/call-log/${logId}/recording`,
    getCallLogs: (id: string) => `crm/leads/${id}/call-logs`,
    getAllCallLogs: () => 'crm/call-logs',
    getLeadTimeline: (id: string) => `crm/leads/${id}/timeline`,
    getTelecallers: () => 'crm/telecallers',
    createTelecaller: () => 'crm/telecallers',
    getUnlinkedEmployees: () => 'crm/telecallers/unlinked-employees',
    linkEmployeeAsTelecaller: () => 'crm/telecallers/link-employee',
    updateTelecaller: (id: string) => `crm/telecallers/${id}`,
    deleteTelecaller: (id: string) => `crm/telecallers/${id}`,
    getAdminDashboard: (date_from?: string, date_to?: string) => {
      const p = new URLSearchParams();
      if (date_from) p.set('date_from', date_from);
      if (date_to) p.set('date_to', date_to);
      const q = p.toString();
      return q ? `crm/dashboard/admin?${q}` : 'crm/dashboard/admin';
    },
    getTelecallerDashboard: (telecallerId: string) => `crm/dashboard/telecaller/${telecallerId}`,
    getFollowups: (telecaller_id?: string, overdue_only?: boolean) => {
      const p = new URLSearchParams();
      if (telecaller_id) p.set('telecaller_id', telecaller_id);
      if (overdue_only) p.set('overdue_only', 'true');
      const q = p.toString();
      return q ? `crm/followups?${q}` : 'crm/followups';
    },
    completeFollowup: (id: string) => `crm/followups/${id}/complete`,
    getDailyReport: (date?: string) => date ? `crm/reports/daily?date=${date}` : 'crm/reports/daily',
    getWeeklyReport: (week_start?: string) => week_start ? `crm/reports/weekly?week_start=${week_start}` : 'crm/reports/weekly',
    getPerformanceReport: (p?: { date_from?: string; date_to?: string; telecaller_id?: string }) => {
      const q = new URLSearchParams(Object.entries(p||{}).filter(([,v])=>v).map(([k,v])=>[k,v as string])).toString();
      return q ? `crm/reports/performance?${q}` : 'crm/reports/performance';
    },
    getLeadScoreSummary: () => 'crm/reports/lead-score-summary',
    exportDeadLeads: () => 'crm/leads/dead/export',
    shuffleUnassignedLeads: () => 'crm/leads/shuffle-unassigned',
    getWhatsAppTemplates: () => 'crm/whatsapp-templates',
    createWhatsAppTemplate: () => 'crm/whatsapp-templates',
    updateWhatsAppTemplate: (id: string) => `crm/whatsapp-templates/${id}`,
    deleteWhatsAppTemplate: (id: string) => `crm/whatsapp-templates/${id}`,
    markWhatsAppSent: (id: string) => `crm/leads/${id}/whatsapp-sent`,
    sendLeadWhatsApp: (id: string) => `crm/leads/${id}/send-whatsapp`,
    draftLeadWhatsAppMessage: (id: string) => `crm/leads/${id}/draft-message`,
    getOverdueNotifications: () => 'crm/notifications/overdue',
    assignLeadSafe: (id: string) => `crm/leads/${id}/assign-safe`,
    getWASessionStatus: () => 'whatsapp-session/status',
    syncFacebook: () => 'crm/sync-facebook',
    backfillFormNames: () => 'crm/backfill-form-names',
    getApiKey: () => 'crm/my-api-key',
    getMyCrmContext: () => 'crm/my-context',
    getStatuses: () => 'crm/statuses',
    createStatus: () => 'crm/statuses',
    updateStatus: (id: string) => `crm/statuses/${id}`,
    deleteStatus: (id: string) => `crm/statuses/${id}`,
    getTags: () => 'crm/tags',
    createTag: () => 'crm/tags',
    updateTag: (id: string) => `crm/tags/${id}`,
    deleteTag: (id: string) => `crm/tags/${id}`,
    setLeadTags: (id: string) => `crm/leads/${id}/tags`,
    bulkSendWhatsApp: () => 'crm/leads/bulk-whatsapp',
    optOutLeadWhatsApp: (id: string) => `crm/leads/${id}/whatsapp-opt-out`,
    optInLeadWhatsApp: (id: string) => `crm/leads/${id}/whatsapp-opt-in`,
    getWhatsAppBulkJobs: () => 'crm/whatsapp-bulk-jobs',
    getWhatsAppBulkJobDetail: (id: string) => `crm/whatsapp-bulk-jobs/${id}`,
  },

  VerifyWholesaler: {
    verifyWholesaler: (userId: string) => {
      return `/user/wholesaller/${userId}/verification`;
    },
  },

  AdminVendor: {
    AddVendors: () => {
      return `user/vendor`;
    },
    getvendorsList: (page?: number, limit?: number, search?: string) => {
      const params = new URLSearchParams();
      if (typeof page === 'number') params.set('page', String(page));
      if (typeof limit === 'number') params.set('limit', String(limit));
      if (search?.trim()) params.set('search', search.trim());
      const query = params.toString();
      return query ? `user/adminuser-list?${query}` : 'user/adminuser-list';
    },
    updateVendorStatus: (vendorId: string) => {
      return `user/vendor/${vendorId}/status`;
    },
  },

  VendorProducts: {
    getVendorProductsList: (status: string) => {
      return `product/get-product-vendor/${status}`;
    },
    statusUpdateVendorProducts: (productId: string) => {
      return `product/approve-vendor-product/${productId}`;
    },
  },

  commission: {
    addCommissionList: () => {
      return `/theme/add-themecategory-details`;
    },
    getCommissionList: () => {
      return `/theme/get-themecategory-details`;
    },
  },

  instagramAccount: {
    createInstagramAccount: () => {
      return `instagram/save-fb-details`;
    },
    getAllInstagramAccount: () => {
      return `instagram/get-fb-details`;
    },
  },

  instagramCampaignPage: {
    instagramcampaignList: () => {
      return `instagram/get-campaigns`;
    },

    instagramaddCampaign: () => {
      return `instagram/campaign`;
    },
  },

  instagramAdsetPage: {
    instagramAdsetList: () => {
      return `instagram/get-adsets`;
    },
    instagramAdsetAdd: () => {
      return `instagram/adset`;
    },
    instagramAdsetStatus: () => {
      return `instagram/adset/update-status`;
    },
  },

  instagramCreativePage: {
    instagramCreativeList: () => {
      return `instagram/get-creative`;
    },

    instagramCreativeAdd: () => {
      return `instagram/creative`;
    },
  },

  instagramAdsPage: {
    instagramAdsList: () => {
      return `instagram/get-ads`;
    },

    instagramAdsAdd: () => {
      return `instagram/create-ad`;
    },
  },

  productImageSearch: {
    getProductImageSearch: () => {
      return `/search-image`;
    },
  },
  vibemartThemeCategory: {
    addVibemartThemeTopCategory: (categoryId: string) => {
      return `category/update-category-priority/${categoryId}`;
    },
    getVibemartThemeTopCategory: () => {
      return `category/priority-categories`;
    },
  },

  metaData: {
    addMetaData: () => {
      return `logo/vendor/meta`;
    },

    getMetaData: () => {
      return `logo/vendor/meta`;
    },

    updateMetaData: (metaId: string) => {
      return `logo/vendor/meta/${metaId}`;
    },
  },

  employeeOrderData: {
    getEmployeeOrderList: (wholesalerId: string, status: string) => {
      return `order/get-orders/${wholesalerId}?status=${status}`;
    },
  },

  searchDomain: {
    vendorDomainSearch: () => {
      return `domainprovider/search-domain`;
    },

    vendorDomainBuy: () => {
      return `domainprovider/buy-domain`;
    },

    discoverDomainConnect: (domain: string) => {
      return `domainprovider/domain-connect?domain=${encodeURIComponent(domain)}`;
    },

    getDominDetail: (domain: string) => {
      return `domainprovider/domain-details?domain=${domain}`;
    },
  },

  AppForm: {
    addAppFormData: () => {
      return `create-build`;
    },
  },

  appList: {
    getAppList: () => `/app-detail/get-app-detail`,
    saveAppDetails: () => `/app-detail/add-app-detail`,
  },

  resellerGoogleAuth: {
    resellerLoginWithGoogle: () => {
      return `/user/google-login`;
    },
    resellerSignupWithGoogle: () => {
      return `/user/google-signup`;
    },
    resellerLinkGoogle: () => {
      return `/user/google-link`;
    },
    resellerCompleteGoogleSignup: () => {
      return `/user/google-signup-complete`;
    },
  },

  resellerFacebookAuth: {
    resellerLoginWithFacebook: () => {
      return `/user/facebook-login`;
    },
    resellerSignupWithFacebook: () => {
      return `/user/facebook-signup`;
    },
  },

LoginWithWhatsapp:{
    sendOtp:()=>{
    return `whatsapp-login/send-otp`
  },

  verifyOtp:()=>{
    return `whatsapp-login/verify-otp`
  }

},


LoginWithNumber:{
    sendOtp:()=>{
    return `user/admin-login-with-otp`;
  },

  verifyOtp:()=>{
    return `user/admin-verify-otp`;
  }

},

OpenAiResponse:{
  aiGenerateResponse:()=>{
    return `openai/generate-response`;
  },
  aiImportProduct:()=>{
    return `openai/import-product`;
  }
},

SubscriptionTransaction:{
  subscriptiontUpgrade:()=>{
    return `wallet/subscription/transaction`;
  }
},

enquiry: {
  getEnquiries: () => {
    return `enquires/get-enquiries`;
  },
},

BlogSerives:{
  addBlogData:()=>{
    return `blog/create-blog`;
  },
    getBlogData:()=>{
    return `blog/get-blogs`;
  },
  deleteBlogData: (id: string) => `blog/delete-blog/${id}`,
},

  employeeKpi: {
    getTemplates: () => 'employee-kpi/templates',
    saveTemplate: () => 'employee-kpi/templates',
    updateTemplate: (id: string) => `employee-kpi/templates/${id}`,
    deleteTemplate: (id: string) => `employee-kpi/templates/${id}`,
    getAssignments: (employeeId: string) => `employee-kpi/assignments/${employeeId}`,
    upsertAssignment: (employeeId: string, templateId: string) => `employee-kpi/assignments/${employeeId}/${templateId}`,
    logKpi: () => 'employee-kpi/log',
    getPerformance: (employeeId: string, period_date?: string, period_type?: string) => {
      const q = new URLSearchParams();
      if (period_date) q.set('period_date', period_date);
      if (period_type) q.set('period_type', period_type);
      const qs = q.toString();
      return qs ? `employee-kpi/performance/${employeeId}?${qs}` : `employee-kpi/performance/${employeeId}`;
    },
    getTeamPerformance: (period_date?: string, period_type?: string) => {
      const q = new URLSearchParams();
      if (period_date) q.set('period_date', period_date);
      if (period_type) q.set('period_type', period_type);
      const qs = q.toString();
      return qs ? `employee-kpi/performance/team?${qs}` : 'employee-kpi/performance/team';
    },
    clockIn: () => 'employee-kpi/attendance/checkin',
    clockOut: () => 'employee-kpi/attendance/checkout',
    markAttendance: () => 'employee-kpi/attendance/mark',
    getAttendance: (employeeId: string, month?: string) =>
      month ? `employee-kpi/attendance/${employeeId}?month=${month}` : `employee-kpi/attendance/${employeeId}`,
  },

});
